import { useCallback, useEffect, useState } from "react";
import { FlowTemplateAction, useFlows } from "../../../../../stores/flowsStore";
import { useFlowsTemplates } from "../../../../../stores/flowsTemplatesStore";

interface UseTemplateConfigurationProps {
  templateAction: FlowTemplateAction | null;
  onChange: (templateAction: FlowTemplateAction | null) => void;
}

export const useTemplateConfiguration = ({
  templateAction,
  onChange,
}: UseTemplateConfigurationProps) => {
  const { templates, fetchTemplates, templatesLoading } = useFlowsTemplates();
  const { generatedDocumentsForFlows, fetchGeneratedDocumentsForFlows } = useFlows();

  // Determine initial source type from existing templateAction
  const [sourceType, setSourceType] = useState<"template" | "generated_document">(
    templateAction?.source_type === "generated_document" ? "generated_document" : "template"
  );

  useEffect(() => {
    fetchTemplates();
    fetchGeneratedDocumentsForFlows();
  }, [fetchTemplates, fetchGeneratedDocumentsForFlows]);

  // Update sourceType when templateAction changes from outside
  useEffect(() => {
    if (templateAction?.source_type) {
      setSourceType(templateAction.source_type as "template" | "generated_document");
    }
  }, [templateAction?.source_type]);

  const handleSourceTypeChange = useCallback(
    (
      _event: React.MouseEvent<HTMLElement>,
      newSourceType: "template" | "generated_document" | null
    ) => {
      if (newSourceType) {
        setSourceType(newSourceType);
        // Clear the current selection when switching types
        onChange({
          source_type: newSourceType,
          template_id: undefined,
          template_name: undefined,
          document_id: undefined,
          document_name: undefined,
        });
      }
    },
    [onChange]
  );

  const handleTemplateChange = useCallback(
    (templateId: string) => {
      const template = templates.find((t) => t.identifier === templateId);
      onChange({
        source_type: "template",
        template_id: templateId,
        template_name: template?.name || "",
        document_id: undefined,
        document_name: undefined,
      });
    },
    [templates, onChange]
  );

  const handleDocumentChange = useCallback(
    (documentId: string) => {
      const document = generatedDocumentsForFlows.find((d) => d.id === documentId);
      onChange({
        source_type: "generated_document",
        template_id: undefined,
        template_name: undefined,
        document_id: documentId,
        document_name: document?.name || "",
      });
    },
    [generatedDocumentsForFlows, onChange]
  );

  return {
    templates,
    templatesLoading,
    generatedDocumentsForFlows,
    sourceType,
    handleSourceTypeChange,
    handleTemplateChange,
    handleDocumentChange,
  };
};
